import { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = logged out
  const [accessToken, setAccessToken] = useState(() => localStorage.getItem("mb_access"));
  const [refreshToken, setRefreshToken] = useState(() => localStorage.getItem("mb_refresh"));

  const saveTokens = (access, refresh) => {
    localStorage.setItem("mb_access", access);
    localStorage.setItem("mb_refresh", refresh);
    setAccessToken(access);
    setRefreshToken(refresh);
  };

  const clearTokens = () => {
    localStorage.removeItem("mb_access");
    localStorage.removeItem("mb_refresh");
    setAccessToken(null);
    setRefreshToken(null);
  };

  const login = async (email, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password });
    saveTokens(data.access_token, data.refresh_token);
    const u = { id: data.id, email: data.email, name: data.name, role: data.role };
    setUser(u);
    return u;
  };

  const logout = useCallback(() => {
    clearTokens();
    setUser(false);
  }, []);

  const refresh = useCallback(async () => {
    const stored = localStorage.getItem("mb_refresh");
    if (!stored) throw new Error("no refresh token");
    const { data } = await axios.post(`${API}/auth/refresh`, { refresh_token: stored });
    localStorage.setItem("mb_access", data.access_token);
    setAccessToken(data.access_token);
    return data.access_token;
  }, []);

  const authFetch = useCallback(
    async (fn) => {
      try {
        return await fn(accessToken);
      } catch (e) {
        if (e.response?.status === 401) {
          const token = await refresh();
          return await fn(token);
        }
        throw e;
      }
    },
    [accessToken, refresh]
  );

  useEffect(() => {
    const check = async () => {
      const token = localStorage.getItem("mb_access");
      if (!token) {
        setUser(false);
        return;
      }
      try {
        const { data } = await axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } });
        setUser(data);
      } catch {
        try {
          const fresh = await refresh();
          const { data } = await axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${fresh}` } });
          setUser(data);
        } catch {
          clearTokens();
          setUser(false);
        }
      }
    };
    check();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AuthContext.Provider value={{ user, login, logout, authFetch }}>
      {children}
    </AuthContext.Provider>
  );
}
